/**
 * Scratchpad for the /orders/new form.
 *
 * Why: the new-order flow is intentionally local-first — no Booqable draft
 * exists until the user clicks "Save as draft". That means a refresh or
 * accidental navigation would otherwise wipe whatever they had typed in.
 * The scratchpad persists the editable fields to localStorage so the form
 * survives both. Cleared once a real order id exists.
 */

const KEY = 'twm:new-order-scratch';

export interface OrderDraftScratch {
  customerId: string | null;
  /** Cached display fields so refresh can rehydrate the customer card
   *  synchronously — without these we'd round-trip /booqable/customers/:id
   *  before the field could repopulate, which paints blank for ~200ms. */
  customerName: string | null;
  customerEmail: string | null;
  pickup: string;
  return: string;
  locationAddress: string;
  locationName: string;
  locationLat: number | null;
  locationLng: number | null;
}

const EMPTY: OrderDraftScratch = {
  customerId: null,
  customerName: null,
  customerEmail: null,
  pickup: '',
  return: '',
  locationAddress: '',
  locationName: '',
  locationLat: null,
  locationLng: null,
};

export function readOrderDraftScratch(): OrderDraftScratch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return { ...EMPTY, ...parsed };
  } catch {
    return null;
  }
}

export function writeOrderDraftScratch(value: OrderDraftScratch): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Quota / disabled storage — non-fatal.
  }
}

export function clearOrderDraftScratch(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Non-fatal.
  }
}

export function isEmptyScratch(s: OrderDraftScratch): boolean {
  return (
    !s.customerId &&
    !s.customerName &&
    !s.customerEmail &&
    !s.pickup &&
    !s.return &&
    !s.locationAddress &&
    !s.locationName &&
    s.locationLat == null &&
    s.locationLng == null
  );
}
